import { Identifiable, ItemCompletedOrderRecord, NamedIdentifiable } from "../model/Items";
import useHttp from "../security/Http.hook";

export type PackagingType = 'foil' | 'stretch' | 'string' | 'pallet' | 'none' | 'specification';

export interface Kit
extends NamedIdentifiable { }

export interface KitPackageForm {
  form: KitPackageElement;
  quantity: number;
}

export interface KitPackageShape {
  shape: KitPackageElement;
  quantity: number;
}

interface KitPackageElement
extends Identifiable {
}

export interface KitForm {
  form: Identifiable;
  quantity: number;
}

interface KitShape {
  shape: Identifiable;
  quantity: number;
}

export interface KitPackage {
  id?: number;
  name: string;
  packagingType: PackagingType;
  packageForms: KitPackageForm[];
  packageShapes: KitPackageShape[]
}

export interface KitCompletedOrderRecord
extends ItemCompletedOrderRecord {
  kitId: number;
}

const getKitPackagesUrl = (customerId: number, kitId: number) => `/api/customers/${customerId}/kits/${kitId}/packages`;

const useKitApi = () => {
  const http = useHttp();

  const fetchKits = (customerId: number, priceListId: number): Promise<Kit[]> =>
    http.get(`/api/customers/${customerId}/price-lists/${priceListId}/kits`);

  const deleteKit = (customerId: number, priceListId: number, kitId: number) =>
    http.delete(`/api/customers/${customerId}/price-lists/${priceListId}/kits/${kitId}`);

  const fetchKitFormDefinitions = (customerId: number, kitId: number): Promise<KitForm[]> => {
    return http.get(`/api/customers/${customerId}/kits/${kitId}/forms`);
  };

  const fetchKitShapeDefinitions = (customerId: number, kitId: number): Promise<KitShape[]> => {
    return http.get(`/api/customers/${customerId}/kits/${kitId}/shapes`);
  };

  const fetchPricedKitElements = (customerId: number, priceListId: number, kitId: number, elementName: 'forms'|'shapes') => {
    const kitsResource = `customers/${customerId}/price-lists/${priceListId}/kits`;
    return http.get(`/api/${kitsResource}/${kitId}/${elementName}`);
  };

  const fetchKitForms = (customerId: number, priceListId: number, kitId: number): Promise<KitForm[]> =>
    fetchPricedKitElements(customerId, priceListId, kitId, 'forms');

  const fetchKitShapes = (customerId: number, priceListId: number, kitId: number): Promise<KitShape[]> =>
    fetchPricedKitElements(customerId, priceListId, kitId, 'shapes');

  const fetchKitPackages = (customerId: number, kitId: number): Promise<KitPackage[]> =>
    http.get(getKitPackagesUrl(customerId, kitId));

  const fetchKitCompletedOrders = (customerId: number): Promise<KitCompletedOrderRecord[]> =>
    http.get(`/api/customers/${customerId}/kitcompletedorders`);

  const saveKitPackages = (
    customerId: number,
    kitId: number,
    kitPackages: KitPackage[]
  ) => http.put(getKitPackagesUrl(customerId, kitId), kitPackages);

  const changeKitPackageName = (
    customerId: number,
    kitId: number,
    kitPackageId: number,
    kitPackageName: string
  ) => http.patch(`${getKitPackagesUrl(customerId, kitId)}/${kitPackageId}`, { name: kitPackageName });

  return {
    fetchKits,
    deleteKit,
    fetchKitFormDefinitions,
    fetchKitShapeDefinitions,
    fetchKitForms,
    fetchKitShapes,
    fetchKitPackages,
    fetchKitCompletedOrders,
    saveKitPackages,
    changeKitPackageName
  };
}

export default useKitApi;